import { z } from 'zod';

import { collectionPath, defineModelSpec, orderBy } from '@elara/firebolt/shared';
import { zFirestampOptional } from '@elara/firebolt/zod-types';

import {
  StoredStoryBlocksSchema,
  STORY_SCHEMA_VERSION,
  StoryAuthorIdSchema,
  StoryImageAssetSchema,
  StoryStatusSchema,
} from './schemas/story-schemas.js';
import { id } from './schemas/zod-types.js';

/**
 * The canonical `ModelSpec` for stories (`stories/{id}`), the editorial articles on the trips home.
 *
 * One collection holds every story in every state. Only `status` is stored: a published story is
 * scheduled while `publishAt` is ahead, live once it passes, and expired after `expiresAt`. Clients listen
 * to `status == 'published'` ordered by `publishAt` (index `[status ASC, publishAt DESC]`) and decide on
 * the device which are live, so a scheduled story is already cached when its time comes (and a `where` on
 * `expiresAt` would drop every story without one). The rules let a signed-in user read published stories
 * only; every write goes through the admin flows.
 *
 * The fields are lenient so a draft can be saved half-written; see `story-schemas.ts` for where
 * completeness is enforced.
 */
export const StoryModelSpec = defineModelSpec({
  path: collectionPath`stories/${id}`,
  shape: {
    title: z.string().default('').describe('Article title'),
    description: z.string().default('').describe('One-line summary under the title'),
    author: StoryAuthorIdSchema.describe('Which specialist wrote it'),
    coverImage: StoryImageAssetSchema.nullish().describe('Cover, at every stored width'),
    coverCredit: z.string().nullish().describe('Markdown attribution for the cover'),
    content: StoredStoryBlocksSchema.default([]).describe('Article blocks, read one at a time'),
    status: StoryStatusSchema.default('draft').describe('draft | published | archived'),
    publishAt: zFirestampOptional().describe('When a published story goes live; ahead of now = scheduled'),
    expiresAt: zFirestampOptional().describe('When a published story leaves the rail; unset = never'),
    schemaVersion: z.number().int().positive().default(STORY_SCHEMA_VERSION).describe('Document shape version'),
    updatedBy: z.string().nullish().describe('Uid of the last editor to save it'),
  },
  constraints: [orderBy('publishAt', 'desc')],
});

export type StoryModelSpec = typeof StoryModelSpec;
export type StoryData = StoryModelSpec['DataType'];
